import { ChangeEvent, useCallback, useState } from 'react';

export type FormErrors = Record<string, string | undefined>;

/**
 * Form values as the user will edit them: a field that starts as `null` (money, dates, a picked record)
 * or `[]` (lines, workers) can later hold a real value, so those start types are widened.
 */
export type FormValues<T> = {
  [K in keyof T]: [T[K]] extends [null] ? any : T[K] extends never[] ? any[] : T[K];
};

/**
 * Form state with per-field errors.
 * set('name') returns an onChange handler that accepts a value or an input event.
 * `initial` may be null for a form that is filled once its data has loaded.
 */
export function useForm<T extends Record<string, any> | null>(initial: T) {
  const [form, setForm] = useState<FormValues<T>>(initial as FormValues<T>);
  const [errors, setErrors] = useState<FormErrors>({});

  const set = useCallback(
    (key: keyof NonNullable<T> & string) =>
      (valueOrEvent: any): void => {
        const target = (valueOrEvent as ChangeEvent<HTMLInputElement> | undefined)?.target;
        const v = target ? (target.type === 'checkbox' ? target.checked : target.value) : valueOrEvent;
        setForm((f) => ({ ...f, [key]: v }) as FormValues<T>);
        setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
      },
    [],
  );

  return { form, setForm, set, errors, setErrors };
}
